import React, { useState, useRef, useEffect } from 'react';
import { Plus, PenLine, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Contact } from '../types';
import ContactForm from './ContactForm';
import BusinessCardScanner from './BusinessCardScanner';

const FixedFAB: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [mode, setMode] = useState<'none' | 'manual' | 'scan'>('none');
  const [scannedData, setScannedData] = useState<Partial<Contact> | null>(null);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  const handleSubmit = (contact: Contact) => {
    setMode('none');
    setScannedData(null);
    setIsExpanded(false);
    navigate(`/contacts/${contact.id}`);
  };

  const handleCancel = () => {
    setMode('none');
    setScannedData(null);
  };

  const handleScanComplete = (contact: Partial<Contact>) => {
    setScannedData(contact);
    setMode('manual'); // Open the contact form pre-filled with scanned data
  };

  return (
    <>
      <div ref={menuRef} className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-30">
        {/* Expanding menu options */}
        <div
          className={`absolute bottom-16 right-0 flex flex-col gap-2 items-end transition-all duration-200 ${
            isExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          <button
            onClick={() => { setMode('scan'); setIsExpanded(false); }}
            className="flex items-center gap-2 bg-white text-gray-800 pl-3 pr-4 py-2.5 rounded-full shadow-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <Camera className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">Scan Card</span>
          </button>
          <button
            onClick={() => { setMode('manual'); setIsExpanded(false); }}
            className="flex items-center gap-2 bg-white text-gray-800 pl-3 pr-4 py-2.5 rounded-full shadow-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <PenLine className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">Manual Entry</span>
          </button>
        </div>

        {/* FAB button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-14 h-14 bg-primary hover:bg-primary-dark text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 ${
            isExpanded ? 'rotate-45' : ''
          }`}
          aria-label="Add new contact"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Scanner modal */}
      {mode === 'scan' && (
        <BusinessCardScanner
          onScanComplete={handleScanComplete}
          onCancel={handleCancel}
        />
      )}

      {/* Contact form modal */}
      {mode === 'manual' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center sm:p-4">
          <div className="modal-mobile bg-white shadow-xl overflow-y-auto max-w-2xl w-full rounded-lg">
            <ContactForm
              initialData={scannedData || undefined}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FixedFAB;
